import {
  newHonorariumClientRowId,
  type U3129FindProjectItem,
  type U3129HonorariumItem,
  type U3129HonorariumTaskRef,
  type U3129ResolveHonorariumTask,
  type U3129TaskItem,
} from "../U3129Service";
import { u3129ProjectDisplayLabel } from "./u3129BuildHonorariumLinesFromTasks";

const TEMPLATE_VERSION = 1 as const;

/** Une ligne de modèle : structure projet / sous-projet / tâche sans heures de semaine. */
export type U3129HonorariumTemplateRow = {
  projectId: string;
  subprojectId: string;
  taskType: string;
  project?: U3129HonorariumItem["project"] | U3129FindProjectItem;
  subproject?: U3129HonorariumItem["subproject"];
  task?: U3129HonorariumItem["task"];
  /** Métadonnées tâche complètes (localStorage — ignorées par l’API U3129). */
  taskCode?: string;
  taskDescriptionFr?: string;
  taskDescriptionEn?: string;
};

export type U3129HonorariumTemplatePayload = {
  version: typeof TEMPLATE_VERSION;
  rows: U3129HonorariumTemplateRow[];
};

export function getHonorariumTemplateStorageKey(userName: string, organizationId: string): string {
  return `U3129_${userName}_${organizationId || "noorg"}_honorariumTemplate`;
}

function resolveProjectId(row: U3129HonorariumItem): string {
  const pid = row.projectId?.trim();
  if (pid) return pid;
  const p = row.project;
  if (p && typeof p === "object" && "id" in p) {
    return String((p as { id?: string }).id ?? "").trim();
  }
  return "";
}

function resolveSubprojectId(row: U3129HonorariumItem): string {
  const sid = row.subprojectId?.trim();
  if (sid) return sid;
  const sp = row.subproject;
  if (sp && typeof sp === "object" && "id" in sp) {
    return String((sp as { id?: string }).id ?? "").trim();
  }
  return "";
}

function resolveTaskType(row: U3129HonorariumItem): string {
  return row.taskType?.trim() || row.task?.id?.trim() || "";
}

/** Clé unique d’une ligne modèle : projet + sous-projet + tâche (pas le seul projectId). */
export function u3129HonorariumTemplateRowKey(row: Pick<U3129HonorariumTemplateRow, "projectId" | "subprojectId" | "taskType">): string {
  const pid = row.projectId?.trim() ?? "";
  const sp = row.subprojectId?.trim() ?? "";
  const tid = row.taskType?.trim() ?? "";
  return `${pid}|${sp}|${tid}`;
}

/** Clé scope projet / sous-projet (alignée sur `u3129BuildFindTasksForProjectCacheKey`). */
export function u3129HonorariumTemplateScopeKey(row: Pick<U3129HonorariumTemplateRow, "projectId" | "subprojectId">): string {
  const pid = row.projectId?.trim() ?? "";
  const sub = row.subprojectId?.trim() ?? "";
  return `${pid}::${sub}`;
}

export type U3129HonorariumTemplateScopeResolution =
  | { status: "usable"; taskIds: Set<string> }
  | { status: "unusable" };

/** Sépare les lignes modèle selon les tâches actives assignées retournées par `findTasksForProject`. */
export function u3129PartitionHonorariumTemplateRowsByActiveAssignments(
  rows: U3129HonorariumTemplateRow[],
  resolutions: Map<string, U3129HonorariumTemplateScopeResolution>,
): { kept: U3129HonorariumTemplateRow[]; ignoredInactive: U3129HonorariumTemplateRow[] } {
  const kept: U3129HonorariumTemplateRow[] = [];
  const ignoredInactive: U3129HonorariumTemplateRow[] = [];
  for (const row of rows) {
    const scopeKey = u3129HonorariumTemplateScopeKey(row);
    const resolution = resolutions.get(scopeKey);
    if (!resolution || resolution.status === "unusable") {
      ignoredInactive.push(row);
      continue;
    }
    const taskType = row.taskType?.trim() ?? "";
    if (!taskType || !resolution.taskIds.has(taskType)) {
      ignoredInactive.push(row);
      continue;
    }
    kept.push(row);
  }
  return { kept, ignoredInactive };
}

/** Corps `findTasksForProject` distincts pour un modèle (un appel par scope). */
export function u3129UniqueFindTasksBodiesFromTemplateRows(
  rows: U3129HonorariumTemplateRow[],
): Array<{ scopeKey: string; body: { projectId: string; subprojectId: string | null } }> {
  const seen = new Map<string, { projectId: string; subprojectId: string | null }>();
  for (const row of rows) {
    const projectId = row.projectId?.trim() ?? "";
    if (!projectId) continue;
    const scopeKey = u3129HonorariumTemplateScopeKey(row);
    if (seen.has(scopeKey)) continue;
    const sub = row.subprojectId?.trim() ?? "";
    seen.set(scopeKey, { projectId, subprojectId: sub ? sub : null });
  }
  return [...seen.entries()].map(([scopeKey, body]) => ({ scopeKey, body }));
}

function u3129TemplateRowRichnessScore(row: U3129HonorariumTemplateRow): number {
  let score = 0;
  if (row.taskCode?.trim() || row.taskDescriptionFr?.trim() || row.taskDescriptionEn?.trim()) score += 4;
  else if (row.taskType?.trim()) score += 2;
  if (row.task?.description?.trim()) score += 1;
  return score;
}

/** Retire les doublons exacts (même projet / sous-projet / tâche), garde l’entrée la plus riche. */
export function u3129DedupeHonorariumTemplateRows(rows: U3129HonorariumTemplateRow[]): U3129HonorariumTemplateRow[] {
  const byKey = new Map<string, U3129HonorariumTemplateRow>();
  const order: string[] = [];
  for (const row of rows) {
    const key = u3129HonorariumTemplateRowKey(row);
    const existing = byKey.get(key);
    if (!existing) {
      order.push(key);
      byKey.set(key, row);
      continue;
    }
    if (u3129TemplateRowRichnessScore(row) > u3129TemplateRowRichnessScore(existing)) {
      byKey.set(key, row);
    }
  }
  return order.map((key) => byKey.get(key)!).filter(Boolean);
}

/** Libellé complet persisté dans `task.description` (code + description). */
export function u3129FormatTemplateTaskStorageDescription(task: U3129TaskItem): string {
  const code = task.taskCode?.trim();
  const fr = task.taskDescriptionFr?.trim();
  const en = task.taskDescriptionEn?.trim();
  const desc = fr || en || "";
  if (code && desc) return `${code} — ${desc}`;
  return desc || code || task.id;
}

export type U3129SerializeHonorariumTemplateOptions = {
  /** Résout la tâche depuis le cache findTasksForProject (par ligne). */
  resolveTask?: U3129ResolveHonorariumTask;
};

function u3129BuildTemplateTaskFields(
  row: U3129HonorariumItem,
  resolvedTask: U3129TaskItem | undefined,
): Pick<U3129HonorariumTemplateRow, "task" | "taskCode" | "taskDescriptionFr" | "taskDescriptionEn"> {
  const taskType = resolveTaskType(row);
  if (!taskType) return {};

  if (resolvedTask) {
    return {
      task: {
        id: resolvedTask.id,
        description: u3129FormatTemplateTaskStorageDescription(resolvedTask),
      },
      taskCode: resolvedTask.taskCode,
      taskDescriptionFr: resolvedTask.taskDescriptionFr,
      taskDescriptionEn: resolvedTask.taskDescriptionEn,
    };
  }

  const existing = row.task;
  if (existing?.id) {
    const ref: U3129HonorariumTaskRef = {
      id: existing.id,
      description: existing.description?.trim() || taskType,
    };
    return { task: ref };
  }

  return { task: { id: taskType, description: taskType } };
}

/** Cherche une tâche dans le cache par clientRowId puis dans toutes les listes chargées. */
export function u3129FindTaskInHonorariumCache(
  row: U3129HonorariumItem,
  cacheByClientRowId: Record<string, U3129TaskItem[]>,
): U3129TaskItem | undefined {
  const taskType = resolveTaskType(row);
  if (!taskType) return undefined;

  const clientRowId = row.clientRowId?.trim();
  const fromRow = clientRowId ? cacheByClientRowId[clientRowId] : undefined;
  const direct = fromRow?.find((task) => task.id === taskType);
  if (direct) return direct;

  for (const list of Object.values(cacheByClientRowId)) {
    const match = list.find((task) => task.id === taskType);
    if (match) return match;
  }

  return undefined;
}

/** Extrait les lignes « complètes » (au moins un projet) pour persistance locale. */
export function serializeHonorariumTemplate(
  rows: U3129HonorariumItem[] | undefined | null,
  options?: U3129SerializeHonorariumTemplateOptions,
): U3129HonorariumTemplateRow[] {
  const list = rows ?? [];
  const serialized = list.flatMap((row, rowIndex) => {
    const projectId = resolveProjectId(row);
    if (!projectId) return [];
    const taskType = resolveTaskType(row);
    const resolvedTask = options?.resolveTask?.(row, rowIndex);
    const taskFields = u3129BuildTemplateTaskFields(row, resolvedTask);
    const item: U3129HonorariumTemplateRow = {
      projectId,
      subprojectId: resolveSubprojectId(row),
      taskType,
      project: row.project,
      subproject: row.subproject,
      ...taskFields,
    };
    return [item];
  });
  return u3129DedupeHonorariumTemplateRows(serialized);
}

export function stringifyHonorariumTemplatePayload(rows: U3129HonorariumTemplateRow[]): string {
  const payload: U3129HonorariumTemplatePayload = { version: TEMPLATE_VERSION, rows };
  return JSON.stringify(payload);
}

/** Événement fenêtre émis après écriture du modèle (même onglet — `storage` ne se déclenche pas). */
export const U3129_HONORARIUM_TEMPLATE_CHANGED_EVENT = "u3129-honorarium-template-changed";

/** Persiste le modèle honoraires et notifie U3129 / U3147 dans le même onglet. */
export function writeHonorariumTemplateToStorage(storageKey: string, rows: U3129HonorariumTemplateRow[]): boolean {
  try {
    const deduped = u3129DedupeHonorariumTemplateRows(rows);
    localStorage.setItem(storageKey, stringifyHonorariumTemplatePayload(deduped));
    window.dispatchEvent(
      new CustomEvent(U3129_HONORARIUM_TEMPLATE_CHANGED_EVENT, { detail: { storageKey, rowCount: deduped.length } }),
    );
    return true;
  } catch {
    return false;
  }
}

export function parseHonorariumTemplatePayload(raw: string | null): U3129HonorariumTemplateRow[] | null {
  if (raw == null || raw.trim() === "") return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const obj = parsed as { version?: number; rows?: unknown };
    if (obj.version !== TEMPLATE_VERSION || !Array.isArray(obj.rows)) return null;
    return obj.rows as U3129HonorariumTemplateRow[];
  } catch {
    return null;
  }
}

/** Reconstruit des lignes formulaire (nouveaux `clientRowId`, heures à zéro). */
export function templateRowsToHonorariumItems(rows: U3129HonorariumTemplateRow[]): U3129HonorariumItem[] {
  return rows.map((r) => {
    const projectId = r.projectId?.trim() || "";
    const projectDisplay = u3129ProjectDisplayLabel(r.project, projectId);
    return {
      clientRowId: newHonorariumClientRowId(),
      projectId,
      subprojectId: r.subprojectId || "",
      taskType: r.taskType || "",
      project: r.project,
      subproject: r.subproject,
      task: r.task,
      projectDisplay,
      monday: 0,
      tuesday: 0,
      wednesday: 0,
      thursday: 0,
      friday: 0,
      saturday: 0,
      sunday: 0,
    } as U3129HonorariumItem & { projectDisplay?: string };
  });
}
